/* ==========================================================================
   BeyondCarat diamond filters — bottom-sheet drawer, in-place (Section
   Rendering API) grid updates, and live carat re-pricing.
   Pairs with snippets/bc-diamond-filters.liquid + snippets/bc-card-price-data.liquid.
   Depends on window.BCPricing (assets/pricing-core.js).
   ========================================================================== */
(function () {
  'use strict';

  var CARAT_KEY = 'bc_display_carat';
  var DEFAULT_CARAT = '1';

  function money(cents) {
    var dollars = Math.round((cents || 0) / 100);
    return '$' + dollars.toLocaleString('en-US');
  }
  function parseCarat(str) {
    var n = parseFloat(String(str == null ? '' : str));
    return isFinite(n) && n > 0 ? n : null;
  }

  /* ---- Shared card breakpoints + state (module scope so both the drawer's
     init(root) and the document-wide enhanceCards read one source) ---------- */
  // Card-hover slider breakpoint (Keyzar desktop hover is >=990px only).
  var bcDeskMQ = window.matchMedia('(min-width: 990px)');
  // Layout breakpoint governing where the metal-dots row sits (title row on
  // desktop, after the price on mobile) — mirrors the drawer's 750px layout MQ.
  var bcLayoutMQ = window.matchMedia('(min-width: 750px)');
  var bcIsDesktop = bcLayoutMQ.matches;

  // Selected display carat, read from the drawer's carat group when present
  // (one per page); defaults to DEFAULT_CARAT on surfaces with no drawer.
  function currentCarat() {
    var a = document.querySelector('[data-bc-carat-group] .bc-carat-item.is-active');
    return a ? a.getAttribute('data-carat') : DEFAULT_CARAT;
  }

  // A card is "diamond" (gets the enhancement) iff it carries its own price
  // JSON. Team A emits <script class="bc-card-price"> inside every diamond
  // card-wrapper; legacy collection markup emitted it as a grid__item sibling,
  // so fall back to that scope. Non-diamond cards → null → stay plain Dawn cards.
  function cardPriceScript(card) {
    if (!card || !card.querySelector) return null;
    var s = card.querySelector('script.bc-card-price');
    if (s) return s;
    var li = card.closest ? card.closest('.grid__item') : null;
    var scope = li || card.parentNode;
    return scope && scope.querySelector ? scope.querySelector('script.bc-card-price') : null;
  }
  function isDiamondCard(card) { return !!cardPriceScript(card); }

  // Affirm "as low as" line for a collection card, from its LIVE displayed price
  // (prefers the sale price). Divisor 2842 matches snippets/affirm-as-low-as.liquid
  // (monthly = priceCents / 2842); rounded to the nearest dollar (not floored).
  // Module-scoped so repriceAll (carat) and swapCardMetal (metal) can re-run it.
  function setCardAffirm(card) {
    if (!card) return;
    var el = card.querySelector('[data-bc-affirm]');
    if (!el) return;
    var priceEl = card.querySelector('.price__sale .price-item--sale')
      || card.querySelector('.price__regular .price-item--regular')
      || card.querySelector('span.price-item--regular')
      || card.querySelector('.price-item--regular:not(s)');
    if (!priceEl) return;
    // "$1,249.00" → 1249.00 dollars. Stripping ALL non-digits would fold the
    // decimals into the integer (124900 "dollars").
    var dollars = parseFloat(String(priceEl.textContent).replace(/[^0-9.]/g, ''));
    if (!isFinite(dollars) || dollars <= 0) return;
    var monthly = Math.round((dollars * 100) / 2842);
    if (monthly < 1) monthly = 1;
    el.textContent = 'As low as $' + monthly.toLocaleString('en-US') + '/mo with Affirm';
    el.hidden = false;
  }

  /* ---- Pricing ---------------------------------------------------------- */
  var priceLists = null;
  function getPriceLists() {
    if (priceLists !== null) return priceLists;
    var el = document.getElementById('bc-price-lists');
    try { priceLists = el ? JSON.parse(el.textContent) : {}; }
    catch (e) { priceLists = {}; }
    if (!priceLists || typeof priceLists !== 'object') priceLists = {};
    return priceLists;
  }
  function nearestKey(matrix, target) {
    var best = null, bestDiff = Infinity;
    for (var k in matrix) {
      if (!Object.prototype.hasOwnProperty.call(matrix, k)) continue;
      var c = parseCarat(k);
      if (c === null) continue;
      var d = Math.abs(c - target);
      if (d < bestDiff) { bestDiff = d; best = k; }
    }
    return best;
  }
  // Config products (custom.product_config) price from their OWN config — embedded
  // prices or the shop priceListV2 (#bc-price-list-v2) — exactly like the PDP:
  // variant + (price(sel) − price(default)), the stone delta converted at the raw
  // presentment rate (diamond-customizer.js getActiveRate/convertMatrixToActiveCurrency).
  var priceListV2;
  function getPriceListV2() {
    if (priceListV2 !== undefined) return priceListV2;
    var el = document.getElementById('bc-price-list-v2');
    try { priceListV2 = el ? JSON.parse(el.textContent) : null; } catch (e) { priceListV2 = null; }
    return priceListV2;
  }
  function activeRate() {
    var c = window.Shopify && window.Shopify.currency;
    var r = c ? parseFloat(c.rate) : NaN;
    return (r && r > 0) ? r : 1;
  }
  function cfgWeightToken(data, caratNum) {
    var axes = (data.cfg && data.cfg.axes) || [];
    for (var i = 0; i < axes.length; i++) {
      if (axes[i] && axes[i].id === 'weight' && axes[i].values) {
        var keys = {};
        for (var j = 0; j < axes[i].values.length; j++) keys[axes[i].values[j]] = 1;
        return nearestKey(keys, caratNum);
      }
    }
    return null;
  }
  function configCardPrice(data, caratNum) {
    var B = window.BCPricing;
    if (!B || typeof B.evaluateConfigCents !== 'function') return null;
    var cfg = typeof B.resolveConfigPricesFromV2 === 'function' ? B.resolveConfigPricesFromV2(data.cfg, getPriceListV2()) : data.cfg;
    var def = cfg && cfg.pricing && cfg.pricing.default;
    var token = cfgWeightToken(data, caratNum);
    if (!def || !token) return null;
    if (token === def.weight) return { isDefault: true, token: token };
    var sel = {};
    for (var k in def) if (Object.prototype.hasOwnProperty.call(def, k)) sel[k] = def[k];
    sel.weight = token;
    var pSel = B.evaluateConfigCents(cfg, sel);
    var pDef = B.evaluateConfigCents(cfg, def);
    if (pDef === null) return null;
    var grade = null;
    if (pSel === null && Array.isArray(cfg.availability) && cfg.availability.length) {
      // The default grade does not exist at this carat (grades per weight: studs are F-G VS at
      // 1 Ct but IGI single grades at 2 Cts+). Price the cheapest grade sold at this carat.
      var cheap = cheapestGradeAt(B, cfg, sel);
      if (!cheap) return null;
      pSel = cheap.cents;
      grade = { color: cheap.color, clarity: cheap.clarity };
    }
    if (pSel === null) return null;
    var r = activeRate();
    return { isDefault: false, token: token, grade: grade, cents: data.base + Math.round(pSel * r) - Math.round(pDef * r) };
  }
  function cheapestGradeAt(B, cfg, sel) {
    var allowed = typeof B.resolveAvailable === 'function' ? B.resolveAvailable(cfg, { weight: sel.weight }) : {};
    var axisVals = function (id) {
      if (allowed[id]) return allowed[id];
      var axes = cfg.axes || [];
      for (var i = 0; i < axes.length; i++) if (axes[i] && axes[i].id === id) return axes[i].values || [];
      return [];
    };
    var colors = axisVals('color'), clarities = axisVals('clarity'), best = null;
    for (var i = 0; i < colors.length; i++) {
      for (var j = 0; j < clarities.length; j++) {
        var s = {};
        for (var k in sel) if (Object.prototype.hasOwnProperty.call(sel, k)) s[k] = sel[k];
        s.color = colors[i]; s.clarity = clarities[j];
        var c = B.evaluateConfigCents(cfg, s);
        if (c !== null && (!best || c < best.cents)) best = { cents: c, color: colors[i], clarity: clarities[j] };
      }
    }
    return best;
  }
  function stoneCentsAt(matrix, caratNum, color, clarity) {
    if (!matrix) return null;
    if (matrix.type === 'per_carat') {
      return window.BCPricing.lookupStoneCents(matrix, caratNum + ' Ct', color, clarity);
    }
    var key = nearestKey(matrix, caratNum);
    if (key === null) return null;
    return window.BCPricing.lookupStoneCents(matrix, key, color, clarity);
  }
  function findPriceEl(card) {
    return card.querySelector('.price__sale .price-item--sale')
      || card.querySelector('#js-sale-price')
      || card.querySelector('.price__regular .price-item--regular')
      || card.querySelector('#js-regular-price')
      || card.querySelector('.price-item--regular:not(s)');
  }
  var __gridMO = null, __gridTarget = null;
  // Keep each card's product link carrying the selected display carat, so the
  // PDP opens pre-configured at that carat (the customizer restores from the
  // URL hash: #weight=<token>&color=<c>&clarity=<cl>). When the selected carat
  // equals the product's own default, the link stays clean (opens as normal).
  function updateCardCaratLink(card, data, caratStr, caratNum, lists, grade) {
    var links = card.querySelectorAll('a[href*="/products/"]');
    if (!links.length) return;
    var defNum = parseCarat(data.defaultCarat);
    var isDefault = (defNum !== null && Math.abs(defNum - caratNum) < 1e-9);
    var hash = '';
    if (!isDefault) {
      var matrix = data.overrideMatrix || (data.priceList ? lists[data.priceList] : null);
      var token = data.cfg ? cfgWeightToken(data, caratNum) : null;
      if (!token && matrix && matrix.type !== 'per_carat') token = nearestKey(matrix, caratNum);
      if (!token) token = caratStr + (caratNum === 1 ? ' Ct' : ' Cts');
      var parts = ['weight=' + encodeURIComponent(token)];
      var linkColor = (grade && grade.color) || data.color;
      var linkClarity = (grade && grade.clarity) || data.clarity;
      if (linkColor) parts.push('color=' + encodeURIComponent(linkColor));
      if (linkClarity) parts.push('clarity=' + encodeURIComponent(linkClarity));
      hash = '#' + parts.join('&');
    }
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.getAttribute('data-bc-href') === null) a.setAttribute('data-bc-href', a.getAttribute('href') || '');
      var orig = a.getAttribute('data-bc-href') || '';
      var hi = orig.indexOf('#');
      var clean = hi >= 0 ? orig.slice(0, hi) : orig;
      a.setAttribute('href', clean + hash);
    }
  }
  function repriceAll(caratStr) {
    if (!window.BCPricing || typeof window.BCPricing.lookupStoneCents !== 'function') return;
    var caratNum = parseCarat(caratStr);
    if (caratNum === null) caratNum = 1;
    var lists = getPriceLists();
    var nodes = document.querySelectorAll('script.bc-card-price');
    // Detach the grid observer while we write prices — our own textContent
    // writes are DOM mutations inside the observed subtree and would otherwise
    // retrigger this same function forever (infinite loop / page hang).
    if (__gridMO) { __gridMO.disconnect(); }
    for (var i = 0; i < nodes.length; i++) {
      var data;
      try { data = JSON.parse(nodes[i].textContent); } catch (e) { continue; }
      if (!data || !data.id) continue;
      var card = document.querySelector('.card-wrapper[data-product-id="' + data.id + '"]');
      if (!card) continue;
      var cp = data.cfg ? configCardPrice(data, caratNum) : null;
      updateCardCaratLink(card, data, caratStr, caratNum, lists, cp && cp.grade);
      var priceEl = findPriceEl(card);
      if (!priceEl) continue;
      if (priceEl.getAttribute('data-bc-orig') === null) {
        priceEl.setAttribute('data-bc-orig', priceEl.textContent.trim());
      }
      var original = priceEl.getAttribute('data-bc-orig');
      if (data.cfg) {
        priceEl.textContent = (!cp || cp.isDefault) ? original : money(cp.cents);
        continue;
      }
      var matrix = data.overrideMatrix || (data.priceList ? lists[data.priceList] : null);
      if (!matrix) { priceEl.textContent = original; continue; }
      var baseStone = window.BCPricing.lookupStoneCents(matrix, data.defaultCarat, data.color, data.clarity);
      if (baseStone === null) { priceEl.textContent = original; continue; }
      var defNum = parseCarat(data.defaultCarat);
      if (defNum !== null && Math.abs(defNum - caratNum) < 1e-9) { priceEl.textContent = original; continue; }
      var targetStone = stoneCentsAt(matrix, caratNum, data.color, data.clarity);
      if (targetStone === null) { priceEl.textContent = original; continue; }
      priceEl.textContent = money(data.base + targetStone - baseStone);
    }
    // Keep every card's Affirm "as low as" line in sync with its new price
    // (still inside the observer-detached window, so no self-retrigger).
    var __affCards = document.querySelectorAll('.card-wrapper');
    for (var __ai = 0; __ai < __affCards.length; __ai++) {
      if (isDiamondCard(__affCards[__ai])) setCardAffirm(__affCards[__ai]);
    }
    if (__gridMO && __gridTarget) { __gridMO.observe(__gridTarget, { childList: true, subtree: true }); }
  }

  /* ======================================================================
     Card enhancement — runs on EVERY surface, not just the filter drawer.
     These were nested inside init(root) and scoped to #product-grid; hoisted
     to module scope + re-scoped to any .card-wrapper so featured / search /
     related / collage cards behave exactly like collection cards. Each acts
     only on a "diamond" card (one carrying its own <script class="bc-card-price">)
     so plain Dawn cards degrade gracefully. Every function is idempotent
     (guards below) so a re-scan after dynamic inserts is safe.
     ====================================================================== */

  // Card metal dots: on desktop drop them into the `.bc-card-title-row` (right
  // of the title); otherwise keep the mobile position after the price.
  function placeMetalsRow(card, row) {
    var titleRow = card.querySelector('.bc-card-title-row');
    if (bcIsDesktop && titleRow) { titleRow.appendChild(row); return; }
    var priceEl = card.querySelector('.price');
    if (priceEl) priceEl.insertAdjacentElement('afterend', row);
    else if (titleRow) titleRow.appendChild(row);
  }
  function repositionAllMetals() {
    var rows = document.querySelectorAll('.card-wrapper .bc-card-metals');
    for (var i = 0; i < rows.length; i++) {
      var card = rows[i].closest ? rows[i].closest('.card-wrapper') : null;
      if (card) placeMetalsRow(card, rows[i]);
    }
  }

  // ===== Sibling map (product id -> per-metal {image, price, url, ...}) =====
  var __siblingMap = null;
  function getSiblingMap() {
    // Every enhanced card (collection grid, search, Load-More pages, grid swaps)
    // emits <script class="bc-card-siblings">{ "<id>": { currentMetal, siblings } }</script>.
    // Absorb every node not seen before (marked on the element), so a grid swap
    // that keeps the same card COUNT still picks up the new cards' entries.
    var nodes = document.querySelectorAll('script.bc-card-siblings');
    if (!__siblingMap) __siblingMap = {};
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__bcSibAbsorbed) continue;
      nodes[i].__bcSibAbsorbed = true;
      var obj;
      try { obj = JSON.parse(nodes[i].textContent); } catch (e) { continue; }
      if (!obj || typeof obj !== 'object') continue;
      for (var id in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, id)) __siblingMap[id] = obj[id];
      }
    }
    return __siblingMap;
  }

  // ===== Finger-following image slider (iPhone-gallery style) =====
  // Images track the finger during a drag, then snap to the nearest image on
  // release. Dawn's images stay absolutely positioned; we drive them with
  // inline translateX transforms so nothing fights the aspect-ratio layout.
  function initCardCarousels(root) {
    root = root || document;
    // Desktop (>=990px) uses the arrow+counter slider from initDesktopCards;
    // this touch/dots slider is for mobile + tablet only.
    if (bcDeskMQ.matches) return;
    root.querySelectorAll('.card__media').forEach(function (cm) {
      var card = cm.closest ? cm.closest('.card-wrapper') : null;
      if (!card || !isDiamondCard(card)) return; // plain Dawn card → leave alone
      // Inside a Dawn `.slider` section (featured/related), the SECTION owns the
      // horizontal swipe — don't hijack it with a per-card touch carousel.
      if (cm.closest && cm.closest('.slider')) return;
      var media = cm.querySelector('.media');
      if (!media || cm.getAttribute('data-bc-carousel')) return;
      // Ignore the src-less desktop-only slides — mobile keeps its loaded images.
      var imgs = media.querySelectorAll(':scope > img:not([data-bc-desktop-slide])');
      var count = imgs.length;
      if (count < 2) return;
      cm.setAttribute('data-bc-carousel', '1');

      var dots = document.createElement('div');
      dots.className = 'bc-card-dots';
      for (var i = 0; i < count; i++) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'bc-card-dot' + (i === 0 ? ' is-active' : '');
        d.setAttribute('data-idx', i);
        d.setAttribute('aria-label', 'Image ' + (i + 1));
        dots.appendChild(d);
      }
      cm.appendChild(dots);

      var active = 0;
      function place(a, dragPx, animate) {
        for (var i = 0; i < count; i++) {
          var im = imgs[i];
          im.style.transition = animate ? 'transform 0.3s ease' : 'none';
          im.style.transform = 'translateX(calc(' + ((i - a) * 100) + '% + ' + (dragPx || 0) + 'px))';
        }
      }
      function setActive(a, animate) {
        active = Math.max(0, Math.min(count - 1, a));
        place(active, 0, animate !== false);
        Array.prototype.forEach.call(dots.children, function (dd, j) { dd.classList.toggle('is-active', j === active); });
      }
      place(0, 0, false);              // initial layout, no animation
      media.__bcSetActive = setActive; // metal-swap resets to the first image

      Array.prototype.forEach.call(dots.children, function (dd) {
        dd.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          setActive(parseInt(dd.getAttribute('data-idx'), 10), true);
        });
      });

      // Drag: image follows the finger; release snaps to nearest (edge resistance).
      var x0 = null, y0 = null, axis = null, dragging = false, w = 0;
      media.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
        axis = null; dragging = false; w = media.clientWidth || cm.clientWidth || 1;
      }, { passive: true });
      media.addEventListener('touchmove', function (e) {
        if (x0 === null) return;
        var dx = e.touches[0].clientX - x0, dy = e.touches[0].clientY - y0;
        if (axis === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (axis !== 'h') return;
        e.preventDefault();            // horizontal drag → no vertical page scroll
        dragging = true;
        var d = dx;
        if ((active === 0 && dx > 0) || (active === count - 1 && dx < 0)) d = dx / 3; // rubber-band at ends
        place(active, d, false);
      }, { passive: false });
      media.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        x0 = null; y0 = null;
        if (axis !== 'h') return;      // tap / vertical: leave the card link alone
        if (dragging && Math.abs(dx) > w * 0.2) setActive(active + (dx < 0 ? 1 : -1), true);
        else place(active, 0, true);   // not far enough → snap back
        axis = null; dragging = false;
      });
    });
  }

  // ===== Per-card metal swatches (White / Yellow / Rose Gold) =====
  var __METALS = ['White Gold', 'Yellow Gold', 'Rose Gold'];
  function initCardMetals(root) {
    root = root || document;
    var map = getSiblingMap();
    if (!map) return;
    root.querySelectorAll('.card-wrapper[data-product-id]').forEach(function (card) {
      if (!isDiamondCard(card)) return;
      if (card.querySelector('.bc-card-metals')) return;
      var entry = map[card.getAttribute('data-product-id')];
      if (!entry || !entry.siblings) return;
      var avail = __METALS.filter(function (m) { return entry.siblings[m]; });
      if (avail.length < 2) return;
      var priceEl = card.querySelector('.price');
      if (!priceEl) return;
      var curImg = card.querySelector('.card__media .media img');
      var curSrc = curImg ? curImg.getAttribute('src') : null;
      var current = null;
      avail.forEach(function (m) { if (entry.siblings[m].image === curSrc) current = m; });
      var row = document.createElement('div');
      row.className = 'bc-card-metals';
      var swatchMap = window.__bcMetalSwatch || {};
      avail.forEach(function (m) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bc-card-metal' + (m === current ? ' is-active' : '');
        b.setAttribute('data-metal', m);
        b.setAttribute('aria-label', m);
        b.title = m;
        if (swatchMap[m]) {
          var im = document.createElement('img');
          im.className = 'bc-metal-swatch-img';
          im.src = swatchMap[m];
          im.alt = m; im.width = 35; im.height = 35; im.loading = 'lazy';
          b.appendChild(im);
        }
        row.appendChild(b);
      });
      placeMetalsRow(card, row);
      row.addEventListener('click', function (e) {
        var b = e.target.closest('.bc-card-metal');
        if (!b) return;
        e.preventDefault(); e.stopPropagation();
        swapCardMetal(card, b.getAttribute('data-metal'));
      });
    });
  }
  function swapCardMetal(card, metal) {
    var map = getSiblingMap();
    var entry = map && map[card.getAttribute('data-product-id')];
    if (!entry || !entry.siblings || !entry.siblings[metal]) return;
    var sib = entry.siblings[metal];
    var media = card.querySelector('.card__media .media');
    var imgs = media ? media.querySelectorAll(':scope > img') : [];
    if (imgs[0]) { if (sib.srcset) imgs[0].setAttribute('srcset', sib.srcset); imgs[0].setAttribute('src', sib.image); }
    if (imgs[1] && sib.hoverImage) { if (sib.hoverSrcset) imgs[1].setAttribute('srcset', sib.hoverSrcset); imgs[1].setAttribute('src', sib.hoverImage); }
    // Update the price AND the baseline repriceAll trusts (data-bc-orig) so a
    // later repriceAll at default carat doesn't revert to the original metal.
    var reg = card.querySelector('.price__regular .price-item--regular');
    if (reg) { reg.textContent = sib.price; reg.setAttribute('data-bc-orig', sib.price); }
    var sale = card.querySelector('.price__sale .price-item--sale');
    if (sale) { sale.textContent = sib.price; sale.setAttribute('data-bc-orig', sib.price); }
    var cmp = card.querySelector('.price__sale s.price-item--regular');
    if (cmp) cmp.textContent = sib.comparePrice || '';
    var pw = card.querySelector('.price');
    if (pw) { if (sib.comparePrice) pw.classList.add('price--on-sale'); else pw.classList.remove('price--on-sale'); }
    if (sib.url) {
      // Also rebase data-bc-href (updateCardCaratLink's baseline) to the sibling
      // URL, so carat re-linking + the ATC resolve the SWAPPED metal's product.
      card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
        a.setAttribute('href', sib.url);
        a.setAttribute('data-bc-href', sib.url);
      });
      var ov = card.querySelector('.card__overlay-link');
      if (ov) { ov.setAttribute('href', sib.url); ov.setAttribute('data-bc-href', sib.url); }
    }
    if (media && media.__bcSetActive) media.__bcSetActive(0, false); // show new metal's main image
    if (media && media.__bcSetSlides) refreshSlidesForMetal(card, media, sib, imgs[1] && sib.hoverImage ? 2 : 1);
    card.querySelectorAll('.bc-card-metal').forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-metal') === metal); });
    setCardAffirm(card); // metal swap changed the price → refresh the Affirm line
  }
  // The sibling data only carries the first two images, so the later slides would keep
  // showing the previous metal. Drop them at once, then load the new metal's own list.
  var __slideLists = {};
  function refreshSlidesForMetal(card, media, sib, keep) {
    card.__bcSlidesDone = true;
    media.__bcSetSlides(keep, []);
    var handle = productHandleFromUrl(sib.url);
    if (!handle) return;
    var token = card.__bcSlideToken = (card.__bcSlideToken || 0) + 1;
    var req = __slideLists[handle] || (__slideLists[handle] =
      fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
        .then(function (p) { return (p && p.media) || []; }));
    req.then(function (all) {
      if (token !== card.__bcSlideToken) return;
      media.__bcSetSlides(keep, all.slice(keep).filter(function (m) { return m.media_type === 'image' && m.src; }).slice(0, 8 - keep));
    }).catch(function () { delete __slideLists[handle]; }); // two correct slides remain
  }

  // ===== Desktop (>=990px) card hover: slider arrows + counter, revealed
  //       action panel (More Info / Add to Cart), lazy image materialization.
  function initDesktopCards(root) {
    root = root || document;
    if (!bcDeskMQ.matches) return;
    var newSliders = [];
    root.querySelectorAll('.card-wrapper').forEach(function (card) {
      if (!isDiamondCard(card)) return;
      if (card.getAttribute('data-bc-deskcard')) return;
      card.setAttribute('data-bc-deskcard', '1');
      var cm = card.querySelector('.card__media');
      var media = cm && cm.querySelector('.media');
      if (cm && media && !cm.getAttribute('data-bc-carousel')) {
        var imgs = media.querySelectorAll(':scope > img');
        if (imgs.length >= 2) newSliders.push(buildDesktopSlider(cm, media, imgs));
      }
      // Materialize placeholder slides + affirm line on first hover/focus.
      card.addEventListener('mouseenter', function () {
        onCardHover(card);
        // A short wait so a pointer crossing the grid doesn't pull every slide of every card.
        if (!card.__bcSlidesDone && !card.__bcSlidesTimer) {
          card.__bcSlidesTimer = setTimeout(function () { materializeSlides(card); }, 150);
        }
      });
      card.addEventListener('mouseleave', function () {
        if (card.__bcSlidesTimer) { clearTimeout(card.__bcSlidesTimer); card.__bcSlidesTimer = null; }
      });
      card.addEventListener('focusin', function () { onCardHover(card); materializeSlides(card); });
      // Arrow pressed before the wait ended (capture: runs before the slide moves).
      card.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.bc-card-arrow')) materializeSlides(card);
      }, true);
      var atc = card.querySelector('[data-bc-atc]');
      if (atc) {
        atc.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          handleAtcClick(card, atc);
        });
      }
    });
    // One style flush for the whole batch (one per card here was 36 forced recalcs on
    // every grid swap), then enable the transitions so the first arrow click slides.
    if (newSliders.length) {
      void getComputedStyle(newSliders[0].media).transform;
      newSliders.forEach(function (s) { s.enable(); });
    }
  }

  function buildDesktopSlider(cm, media, imgs) {
    cm.setAttribute('data-bc-carousel', '1');
    imgs = Array.prototype.slice.call(imgs);
    var count = imgs.length;
    var active = 0;
    // Track-style directional slide: image i sits at (i - active) * 100%.
    // NEXT (active++) shifts the track left → the next image enters from the
    // right (right-to-left); PREV (active--) shifts it right → the previous
    // image enters from the left (left-to-right).
    function setTransform(a) {
      for (var i = 0; i < count; i++) imgs[i].style.transform = 'translateX(' + ((i - a) * 100) + '%)';
    }
    function setTransition(on) {
      for (var i = 0; i < count; i++) imgs[i].style.transition = on ? 'transform 0.35s ease' : 'none';
    }
    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'bc-card-arrow bc-card-arrow--prev';
    prev.setAttribute('aria-label', 'Previous image');
    prev.tabIndex = -1;
    prev.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3 5 8l5 5"/></svg>';
    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'bc-card-arrow bc-card-arrow--next';
    next.setAttribute('aria-label', 'Next image');
    next.tabIndex = -1;
    next.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>';
    var counter = document.createElement('div');
    counter.className = 'bc-card-counter';
    counter.innerHTML = '<span data-bc-cur>1</span> | <span data-bc-total>' + count + '</span>';
    var curEl = counter.querySelector('[data-bc-cur]');
    function update() {
      if (curEl) curEl.textContent = active + 1;
      prev.disabled = active <= 0;
      next.disabled = active >= count - 1;
    }
    function go(a, animate) {
      active = Math.max(0, Math.min(count - 1, a));
      if (animate === false) {
        // Instant (metal-swap reset): disable the transition, move, reflush.
        setTransition(false);
        setTransform(active);
        void media.offsetWidth;
        setTransition(true);
      } else {
        setTransform(active); // transition stays on → animated directional slide
      }
      update();
    }
    prev.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(active - 1, true); });
    next.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(active + 1, true); });
    cm.appendChild(prev);
    cm.appendChild(next);
    cm.appendChild(counter);
    // Initial positions with no animation; initDesktopCards enables the transition
    // after one shared style flush (avoids the transition:none → jump gotcha).
    setTransition(false);
    setTransform(0);
    update();
    media.__bcSetActive = function (a, animate) { go(a, animate); }; // metal swap resets to first image
    // Metal swap: keep the first `keep` slides (already swapped) and replace the rest.
    media.__bcSetSlides = function (keep, list) {
      imgs.slice(keep).forEach(function (im) { im.remove(); });
      imgs = imgs.slice(0, keep);
      var sizes = imgs[0].getAttribute('sizes');
      list.forEach(function (m) {
        var im = document.createElement('img');
        im.setAttribute('data-bc-desktop-slide', '');
        im.alt = m.alt || '';
        if (m.width) im.width = m.width;
        if (m.height) im.height = m.height;
        im.loading = 'lazy';
        if (sizes) im.setAttribute('sizes', sizes);
        var at = function (w) { return m.src + (m.src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w; };
        im.setAttribute('srcset', at(360) + ' 360w, ' + at(533) + ' 533w, ' + at(720) + ' 720w');
        im.setAttribute('src', at(533));
        media.appendChild(im);
        imgs.push(im);
      });
      count = imgs.length;
      var totalEl = counter.querySelector('[data-bc-total]');
      if (totalEl) totalEl.textContent = count;
      go(active, false);
    };
    return { media: media, enable: function () { setTransition(true); } };
  }

  // Materialize the src-less desktop slides (zero requests until now).
  function materializeSlides(card) {
    if (card.__bcSlidesTimer) { clearTimeout(card.__bcSlidesTimer); card.__bcSlidesTimer = null; }
    if (card.__bcSlidesDone) return;
    card.__bcSlidesDone = true;
    var pend = card.querySelectorAll('.card__media .media img[data-bc-src]');
    var mainImg = card.querySelector('.card__media .media img');
    var sizes = mainImg && mainImg.getAttribute('sizes');
    for (var i = 0; i < pend.length; i++) {
      var im = pend[i];
      var ss = im.getAttribute('data-bc-srcset');
      // sizes before srcset: without it the browser assumes 100vw and pulls the largest file.
      if (sizes) im.setAttribute('sizes', sizes);
      if (ss) im.setAttribute('srcset', ss);
      im.setAttribute('src', im.getAttribute('data-bc-src'));
      im.removeAttribute('data-bc-src');
      im.removeAttribute('data-bc-srcset');
      im.classList.remove('bc-slide-pending');
    }
  }

  function onCardHover(card) {
    if (card.__bcHovered) { setCardAffirm(card); return; }
    card.__bcHovered = true;
    setCardAffirm(card);
    measureActionsPanel(card);
  }

  // Affirm "as low as" from the live card price. Divisor matches
  // snippets/affirm-as-low-as.liquid (monthly = priceCents / 2842, i.e.
  // priceDollars / 28.42) — the merchant's empirical figure, not invented.

  // Tell the halo how tall the revealed panel is so it extends to enclose it.
  function measureActionsPanel(card) {
    var panel = card.querySelector('[data-bc-card-actions]');
    if (!panel) return;
    requestAnimationFrame(function () {
      var h = panel.offsetHeight;
      if (h) card.style.setProperty('--bc-actions-h', (h + 4) + 'px');
    });
  }

  // ---- Add to Cart (collection card) -----------------------------------
  // Shopify serves frame-ancestors 'none' + X-Frame-Options: DENY, so a hidden
  // PDP iframe (to harvest the customizer's cart-form properties) is blocked by
  // the browser. Instead: in DEFAULT display state (display carat == the
  // product's defaultCarat) a line with no diamond-spec properties + no
  // _expected_price is left at its variant price by the cart transform — exactly
  // correct — so we add the current variant directly. When the carat is
  // non-default the displayed price differs from the plain variant price, so we
  // navigate to the PDP (pre-configured from the link's #weight/color/clarity
  // hash) and let the real customizer own the add.
  function cardProductUrl(card, atc) {
    var link = card.querySelector('[data-bc-more-info]') || card.querySelector('a[href*="/products/"]');
    var href = (link && link.getAttribute('href')) || (atc && atc.getAttribute('data-product-url')) || '';
    try { return new URL(href, window.location.origin).href; } catch (e) { return href; }
  }
  function productHandleFromUrl(url) {
    try {
      var m = new URL(url, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }
  // Default display state == the current display carat equals this card's
  // product defaultCarat (the same test updateCardCaratLink uses to keep a
  // clean link). data-product-id / bc-card-price are the ORIGINAL product's
  // (unchanged by a metal swap), which is exactly what the display carat is
  // compared against.
  function readCardPriceData(card) {
    var s = cardPriceScript(card);
    if (!s) return null;
    try { return JSON.parse(s.textContent); } catch (e) { return null; }
  }
  function cardIsDefaultCarat(card) {
    var data = readCardPriceData(card);
    if (!data) return true;
    var defNum = parseCarat(data.defaultCarat);
    var curNum = parseCarat(currentCarat());
    if (defNum === null || curNum === null) return true;
    return Math.abs(defNum - curNum) < 1e-9;
  }
  // Normalize exactly like diamond-customizer.js getProductMeta so the card's
  // stamped properties match a PDP add's.
  function normMetal(raw) {
    var m = String(raw == null ? '' : raw).toLowerCase().trim();
    if (m === 'yellow gold') return 'yellow_gold';
    if (m === 'white gold') return 'white_gold';
    if (m === 'rose gold') return 'rose_gold';
    if (m === 'platinum') return 'platinum';
    if (m === 'silver') return 'silver';
    return m.replace(/\s+/g, '_');
  }
  function purityFromVariantTitle(title) {
    var t = String(title == null ? '' : title);
    var m = t.match(/\d{1,2}\s*k/i) || t.match(/platinum|silver|\bpt\b/i);
    return m ? m[0].toLowerCase().replace(/\s+/g, '') : '';
  }
  function activeCardMetal(card) {
    var b = card.querySelector('.bc-card-metal.is-active');
    return b ? b.getAttribute('data-metal') : null; // e.g. "Yellow Gold"
  }
  // Mirror updateFormProperties (~diamond-customizer.js 1595) for a DEFAULT-state
  // card line so the cart line carries the same properties a PDP add sends.
  // NOTE: _pc_selection is intentionally NOT sent (config-product PDP-only; the
  // transform falls back to the matrix path without it).
  function buildCardAddProperties(card, data, variant) {
    var props = {};
    // Metal: the active swatch (reflects a metal swap) wins over the card's
    // original-product server metal; purity from the RESOLVED variant title.
    props['_metal'] = normMetal(activeCardMetal(card) || (data && data.metal) || '');
    var purity = purityFromVariantTitle(variant && variant.title);
    if (purity) props['_metal_purity'] = purity;
    if (data) {
      props['_diamond_shape'] = String(data.shape || '').toLowerCase().trim();
      props['_setting_style'] = String(data.settingStyle || '').toLowerCase().trim().replace(/\s+/g, '_');
      props['_lab_or_natural'] = String(data.labOrNatural || 'lab').toLowerCase().trim();
      props['_collection'] = data.collection || ''; // raw (PDP keeps the collection title as-is)
      // Diamond spec — only the dimensions the PDP would actually emit.
      if (data.hasWeight && data.defaultCarat) {
        props['_diamond_weight'] = data.defaultCarat;
        // Two-stone rings carry the stone split as the visible weight (PDP parity).
        props['Diamond Weight'] = data.weightLabel || data.defaultCarat;
      }
      if (data.hasQuality) {
        if (data.color) { props['_diamond_color'] = data.color; props['Diamond Color'] = data.color; }
        if (data.clarity) { props['_diamond_clarity'] = data.clarity; props['Diamond Clarity'] = data.clarity; }
      }
      // Packed 4C carrier for the cart transform — the second writer of this
      // property; the PDP form is the other (diamond-customizer.js
      // updateFormProperties). The Function's input query is at its complexity-30
      // ceiling and reads this ONE attribute instead of the three above, then
      // re-emits the three. Keep both writers in step: a card add that omits it
      // would price through the _expected_price fail-safe instead of the matrix.
      //
      // Always three segments so a missing field cannot shift the others. A value
      // containing a pipe OMITS the pack rather than stripping the character: a
      // stripped "VS|2" would be sold and manufactured as "VS2", whereas omitting
      // only costs the matrix price (the line falls to the _expected_price
      // fail-safe) and leaves the three human properties carrying the true spec.
      // Built from the props actually set above, so the gating cannot drift.
      var packSegs = [
        String(props['Diamond Weight'] == null ? '' : props['Diamond Weight']),
        String(props['Diamond Color'] == null ? '' : props['Diamond Color']),
        String(props['Diamond Clarity'] == null ? '' : props['Diamond Clarity'])
      ];
      var packHasPipe = packSegs.join('').indexOf('|') !== -1;
      if (!packHasPipe && (packSegs[0] || packSegs[1] || packSegs[2])) {
        props['_bc_4c'] = packSegs.join('|');
      }
    }
    props['_is_customized'] = 'false'; // default-state add
    // Expected + compare-at price in CENTS from the resolved variant = the
    // displayed price (the transform recomputes; _expected_price is its floor).
    if (variant && isFinite(variant.price)) props['_expected_price'] = String(variant.price);
    if (variant && isFinite(variant.compare_at_price) && variant.compare_at_price > variant.price) {
      props['_compare_at_price'] = String(variant.compare_at_price);
    }
    props['_atc_id'] = String(Date.now()) + '-' + Math.random().toString(36).slice(2, 8);
    return props;
  }
  function setAtcState(atc, state, url) {
    var label = atc.querySelector('.bc-card-btn__label');
    atc.classList.remove('is-added', 'is-loading', 'is-fallback');
    if (state === 'loading') {
      atc.setAttribute('data-bc-loading', '1');
      atc.disabled = true;
      if (label) label.textContent = 'Adding…';
    } else if (state === 'added') {
      atc.removeAttribute('data-bc-loading');
      atc.disabled = false;
      atc.classList.add('is-added');
      if (label) label.textContent = 'Added ✓';
      setTimeout(function () {
        atc.classList.remove('is-added');
        if (label) label.textContent = 'Add to Cart';
      }, 2500);
    } else if (state === 'fallback') {
      atc.removeAttribute('data-bc-loading');
      atc.disabled = false;
      atc.classList.add('is-fallback');
      atc.setAttribute('data-bc-fallback-url', url || '');
      if (label) label.textContent = 'View Product';
    } else {
      atc.removeAttribute('data-bc-loading');
      atc.disabled = false;
      if (label) label.textContent = 'Add to Cart';
    }
  }
  function handleAtcClick(card, atc) {
    // After a failure the button becomes a "View Product" link to the PDP.
    if (atc.classList.contains('is-fallback')) {
      var u = atc.getAttribute('data-bc-fallback-url') || cardProductUrl(card, atc);
      if (u) window.location.href = u;
      return;
    }
    if (atc.getAttribute('data-bc-loading') === '1') return;

    var productUrl = cardProductUrl(card, atc); // reflects metal swap + carat hash
    // Non-default carat: the displayed price is carat-modified and stamping
    // spec/_expected_price from the card is unsafe (known metal-swap re-pricing
    // inconsistency). Send the shopper to the PDP, which opens pre-configured
    // from the hash and lets the real customizer own the (re-priced) add.
    if (!cardIsDefaultCarat(card)) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    var handle = productHandleFromUrl(productUrl) || card.getAttribute('data-product-handle');
    if (!handle) { setAtcState(atc, 'fallback', productUrl); return; }
    setAtcState(atc, 'loading');
    // data-variant-id captured at render is stale after a metal swap, so
    // resolve the CURRENT variant from the (possibly swapped) product handle.
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
      .then(function (product) {
        var variant = null, vs = product && product.variants;
        if (vs && vs.length) {
          for (var i = 0; i < vs.length; i++) { if (vs[i].available) { variant = vs[i]; break; } }
        }
        if (!variant) throw new Error('no available variant');
        var props = buildCardAddProperties(card, readCardPriceData(card), variant);
        return addVariantToCart(variant.id, atc, productUrl, props);
      })
      .catch(function () { setAtcState(atc, 'fallback', productUrl); });
  }
  // Default-state add carrying the same line-item properties a PDP add sends
  // (see buildCardAddProperties). The transform recomputes price from the spec,
  // so a full-spec default add stays exactly-priced. Refreshes the cart the same
  // way Dawn's product-form.js does (renderContents + cartUpdate publish).
  function addVariantToCart(variantId, atc, productUrl, properties) {
    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var body = { id: variantId, quantity: 1 };
    if (properties && Object.keys(properties).length) body.properties = properties;
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      body.sections_url = window.location.pathname;
    }
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    // Free gift goes in FIRST when this ring qualifies, so the ring lands on top
    // (see window.BCGift in cart-gift.js). The ring request is unchanged.
    var gift = (window.BCGift && typeof window.BCGift.beforeAdd === 'function')
      ? window.BCGift.beforeAdd({ expectedCents: properties && properties._expected_price, quantity: 1 })
      : { wait: Promise.resolve(), done: function () {} };
    return gift.wait
      .then(function () {
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (resp) {
        gift.done(!(resp && resp.status));
        if (resp && resp.status) { setAtcState(atc, 'fallback', productUrl); return; } // e.g. sold out / not addable
        if (cart && typeof cart.renderContents === 'function') {
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bc-card-atc', productVariantId: variantId, cartData: resp });
            }
          } catch (e) {}
          cart.renderContents(resp); // updates the cart bubble + opens the drawer (same as a PDP add)
          setAtcState(atc, 'added');
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(function (e) { gift.done(false); throw e; }); // no-op if the ring already answered
  }

  /* ---- Init ------------------------------------------------------------- */
  function init(root) {
    // Marks JS Load More as active → CSS hides the server-rendered pagination
    // fallback (kept in the DOM for crawlers / no-JS).
    document.documentElement.classList.add('bc-filters-js');
    var collectionUrl = root.getAttribute('data-collection-url') || window.location.pathname;
    var sectionId = root.getAttribute('data-section-id') || '';
    var metalParam = root.getAttribute('data-metal-param') || 'filter.p.m.custom.metal';
    var styleParam = root.getAttribute('data-style-param') || 'filter.p.m.custom.setting_style';

    var overlay = root.querySelector('[data-bc-overlay]');
    var drawer = root.querySelector('[data-bc-drawer]');
    var openBtn = root.querySelector('[data-bc-open]');
    var closeBtn = root.querySelector('[data-bc-close]');
    var resetBtn = root.querySelector('[data-bc-reset]');
    var applyBtn = root.querySelector('[data-bc-apply]');
    var countEls = root.querySelectorAll('[data-bc-count]');
    var sortSel = root.querySelector('[data-bc-sort]');
    var caratBtns = root.querySelectorAll('[data-bc-carat-group] .bc-carat-item');

    var initData = {};
    var initNode = root.querySelector('[data-bc-init]');
    if (initNode) { try { initData = JSON.parse(initNode.textContent); } catch (e) {} }

    var url = new URL(window.location.href);
    var metalSet = {}; url.searchParams.getAll(metalParam).forEach(function (v) { metalSet[v] = true; });
    var styleSet = {}; url.searchParams.getAll(styleParam).forEach(function (v) { styleSet[v] = true; });

    /* ---- headings ---- */
    function labelsFrom(groupSel, set) {
      var out = [];
      root.querySelectorAll(groupSel + ' [data-value]').forEach(function (b) {
        if (set[b.getAttribute('data-value')]) out.push(b.getAttribute('data-label') || b.getAttribute('data-value'));
      });
      return out.join(', ');
    }
    function refreshHeadings() {
      var shapeV = root.querySelector('[data-bc-shape-value]');
      var metalV = root.querySelector('[data-bc-metal-value]');
      var styleV = root.querySelector('[data-bc-style-value]');
      if (shapeV) shapeV.textContent = initData.currentShape || '';
      if (metalV) metalV.textContent = labelsFrom('[data-bc-metal-group]', metalSet);
      if (styleV) styleV.textContent = labelsFrom('[data-bc-style-group]', styleSet);
    }

    // The "View Results (N)" count must reflect the VISIBLE (primary) products,
    // not collection.products_count — which includes hidden metal/shape siblings
    // (e.g. 120 raw vs 24 shown). Mirror the section's own product count, which
    // already counts only primary products and stays correct after filtering.
    function syncResultCount() {
      var src = document.getElementById('ProductCountDesktop')
             || document.getElementById('ProductCount')
             || document.querySelector('.product-count__text');
      if (!src) return;
      var m = src.textContent.replace(/,/g, '').match(/\d+/);
      if (!m) return;
      root.querySelectorAll('.bc-view-results__count').forEach(function (el) { el.textContent = '(' + m[0] + ')'; });
    }

    /* ---- open / close ---- */
    function open() {
      drawer.hidden = false; overlay.hidden = false;
      void drawer.offsetWidth;
      drawer.classList.add('is-open'); overlay.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('bc-drawer-open'); // hides chat/call + floating btn
    }
    function closeDrawer() {
      drawer.classList.remove('is-open'); overlay.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.body.classList.remove('bc-drawer-open');
      setTimeout(function () { drawer.hidden = true; overlay.hidden = true; }, 340);
    }
    // Bind every "Filters" opener (main bar + floating button).
    root.querySelectorAll('[data-bc-open]').forEach(function (b) { b.addEventListener('click', open); });
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });

    // Floating filter button: show once the main filter bar scrolls out of view.
    var floatingBtn = root.querySelector('[data-bc-floating]');
    var mainBar = root.querySelector('.bc-filter-bar');
    if (floatingBtn && mainBar && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        floatingBtn.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { rootMargin: '0px 0px -40px 0px' }).observe(mainBar);
    }

    /* ---- Section Rendering API swap ---- */
    function stripReveal(el) {
      el.querySelectorAll('.scroll-trigger').forEach(function (n) {
        n.classList.remove('scroll-trigger', 'animate--slide-in');
        n.removeAttribute('data-cascade');
        n.style.removeProperty('--animation-order');
      });
    }
    function fetchSection(rawUrl) {
      var u = new URL(rawUrl, window.location.origin);
      u.searchParams.set('sections', sectionId);
      return fetch(u.pathname + '?' + u.searchParams.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (j) { return new DOMParser().parseFromString(j[sectionId] || '', 'text/html'); });
    }
    // Replace a whole shopify-section (e.g. breadcrumbs / banner) in place. The
    // Section Rendering API returns the section INCLUDING its wrapper, so we
    // swap the element's outerHTML (same id) rather than its innerHTML.
    function swapSection(id, html) {
      if (!html) return;
      var el = document.getElementById('shopify-section-' + id);
      if (el) el.outerHTML = html;
    }
    var suppressObserver = false;
    var suppressTimer = null;
    // Quiet the grid observer for our own writes. One shared timer: an earlier release must
    // not fire while a later fetch is in flight, or its append restarts the list at 24 cards.
    function suppressGridObserver() {
      clearTimeout(suppressTimer);
      suppressObserver = true;
    }
    function releaseGridObserver() {
      clearTimeout(suppressTimer);
      suppressTimer = setTimeout(function () { suppressObserver = false; }, 60);
    }
    // Latest filter/sort/shape request. An older response landing after a newer click
    // would otherwise overwrite the grid (and the URL) with the wrong result.
    var swapSeq = 0;
    var gridWrapEl = document.querySelector('.product-grid-container-wrapper');
    function gridLoading(on) {
      if (!gridWrapEl) return;
      gridWrapEl.style.transition = 'opacity .2s ease';
      gridWrapEl.style.opacity = on ? '0.4' : '';
      gridWrapEl.style.pointerEvents = on ? 'none' : '';
    }
    // Desktop: decode the new first row before the swap, so a filter or sort doesn't flash
    // blank tiles. Capped: a slow image must not hold the grid back.
    function preloadFirstRow(doc) {
      if (!bcDeskMQ.matches) return Promise.resolve();
      var first = doc.querySelectorAll('#product-grid .grid__item .card__media .media > img:first-child');
      var jobs = [];
      for (var i = 0; i < first.length && i < 4; i++) {
        first[i].setAttribute('decoding', 'sync');
        var im = new Image();
        var z = first[i].getAttribute('sizes'), ss = first[i].getAttribute('srcset');
        if (z) im.sizes = z;
        if (ss) im.srcset = ss;
        im.src = first[i].getAttribute('src');
        jobs.push(im.decode ? im.decode().catch(function () {}) : Promise.resolve());
      }
      return Promise.race([Promise.all(jobs), new Promise(function (r) { setTimeout(r, 500); })]);
    }
    function applySwap(doc, displayUrl) {
      suppressGridObserver();
      // Tell the grid section's boot script its in-flight dedup fetch is stale.
      window.__bcGridEpoch = (window.__bcGridEpoch || 0) + 1;
      var fresh = doc.getElementById('ProductGridContainer');
      var stale = document.getElementById('ProductGridContainer');
      if (fresh && stale) { stripReveal(fresh); stale.replaceWith(fresh); }
      ['ProductCountDesktop', 'ProductCount'].forEach(function (id) {
        var f = doc.getElementById(id), s = document.getElementById(id);
        if (f && s) s.textContent = f.textContent;
      });
      syncResultCount();
      // chips + badge come fresh from the re-rendered bar
      var freshChips = doc.querySelector('[data-bc-chips]');
      var curChips = root.querySelector('[data-bc-chips]');
      if (freshChips && curChips) {
        curChips.innerHTML = freshChips.innerHTML;
        curChips.hidden = freshChips.hasAttribute('hidden');
      }
      var freshBadge = doc.querySelector('[data-bc-count]');
      if (freshBadge) {
        var n = freshBadge.textContent.trim();
        countEls.forEach(function (el) { el.textContent = n; el.classList.toggle('is-empty', n === '0'); });
      }
      // The swapped-in chips are server-rendered (metal/style/price only) — re-add
      // the client-side carat chip if a non-default carat is active.
      updateCaratChip(currentCarat());
      if (displayUrl) {
        history.pushState({}, '', displayUrl);
        // One listing_refine per same-path URL change; a DIFFERENT path (shape tile → another
        // collection) is one page_view + view_item_list instead (snippets/gtm-postmessage-bridge.liquid).
        try { window.bcSoftNav && window.bcSoftNav.listingChange('collection_filters', { doc: doc }); } catch (e) {}
      }
      repriceAll(currentCarat());
      // Merge the freshly-fetched sibling map BEFORE initCardMetals runs (inside
      // applyLoadMore) — otherwise new-collection/new-filter grids get card dots
      // from the STALE memoized map (reinitFromDoc only installs it afterwards).
      mergeSiblingMap(doc);
      applyLoadMore(true);
      if (window.BCCollectionFilter && window.BCCollectionFilter.reinitFromDoc) {
        try { window.BCCollectionFilter.reinitFromDoc(doc); } catch (e) {}
      }
      if (window.initImageLoading) window.initImageLoading();
      gridLoading(false);
      releaseGridObserver();
    }

    /* ---- build + apply filters (in place) ---- */
    var CATEGORY_PARAM = 'filter.p.m.custom.category_collection';
    // Collections built from non-primary siblings (platinum-rings, the price
    // bands) render a title-deduped grid server-side and mark the container
    // data-bc-nodedup; adding the category filter there empties the grid.
    function gridNoDedup() {
      var g = document.getElementById('ProductGridContainer');
      return !!(g && g.hasAttribute('data-bc-nodedup'));
    }
    function buildParams() {
      var p = new URLSearchParams();
      // Carry over every param the drawer does NOT own (e.g. a shape_name
      // filter applied via the shape nav or a shared link, or a price filter
      // from a shared link). Rebuilding from drawer state alone would silently
      // clear those — that's how "Round, 135 products" collapsed to 5 cards.
      new URLSearchParams(window.location.search).forEach(function (v, k) {
        if (k === metalParam || k === styleParam) return;
        if (k === 'sort_by' || k === 'display_carat' || k === 'page' || k === 'sections') return;
        if (k === CATEGORY_PARAM) return; // re-derived per request below
        p.append(k, v);
      });
      Object.keys(metalSet).forEach(function (v) { p.append(metalParam, v); });
      Object.keys(styleSet).forEach(function (v) { p.append(styleParam, v); });
      if (sortSel && sortSel.value) p.set('sort_by', sortSel.value);
      return p;
    }
    function applyFilters(close) {
      var p = buildParams();
      var carat = currentCarat();
      var display = new URLSearchParams(p.toString());
      if (carat && carat !== DEFAULT_CARAT) display.set('display_carat', carat);
      // Sibling dedup: fetch one card per design (category_collection=true)
      // unless a sibling-level filter (metal/style) is active — those need the
      // raw siblings, mirroring the siblings_filter_active gate in Liquid.
      var fetchP = new URLSearchParams(p.toString());
      var hasSiblingFilter = Object.keys(metalSet).length > 0 || Object.keys(styleSet).length > 0;
      if (!hasSiblingFilter && !gridNoDedup()) fetchP.set(CATEGORY_PARAM, 'true');
      var qs = fetchP.toString(), dqs = display.toString();
      var displayUrl = collectionUrl + (dqs ? '?' + dqs : '');
      gridLoading(true);
      var seq = ++swapSeq;
      fetchSection(collectionUrl + (qs ? '?' + qs : ''))
        .then(function (doc) {
          return preloadFirstRow(doc).then(function () {
            if (seq !== swapSeq) return; // a newer filter/sort/shape click owns the grid
            applySwap(doc, displayUrl);
            if (close) closeDrawer();
          });
        })
        .catch(function () { if (seq === swapSeq) window.location.href = displayUrl; });
    }

    /* ---- shape → different collection, swapped in place ---- */
    root.querySelectorAll('.bc-shape-item').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var href = a.getAttribute('href');
        var label = a.getAttribute('data-shape');
        var _u = new URL(href, window.location.origin);
        var newBase = _u.pathname;
        // A shape link either points to a dedicated shape sub-collection (rings) or
        // applies a shape filter on the current collection (e.g. earrings, which have
        // no per-shape collection). Carry that filter through the AJAX swap + URL.
        var shapeFilterVal = _u.searchParams.get('filter.p.m.custom.shape_name');
        var carat = currentCarat();
        var sortv = sortSel ? sortSel.value : '';
        var fetchP = new URLSearchParams();
        // A shape FILTER stays on the current collection, so honour its no-dedup
        // mark; a shape COLLECTION is a different page and keeps the dedup.
        if (!(shapeFilterVal && gridNoDedup())) fetchP.set('filter.p.m.custom.category_collection', 'true');
        if (shapeFilterVal) fetchP.set('filter.p.m.custom.shape_name', shapeFilterVal);
        if (sortv) fetchP.set('sort_by', sortv);
        var display = new URLSearchParams();
        if (shapeFilterVal) display.set('filter.p.m.custom.shape_name', shapeFilterVal);
        if (sortv) display.set('sort_by', sortv);
        if (carat && carat !== DEFAULT_CARAT) display.set('display_carat', carat);
        var dqs = display.toString();
        var displayUrl = newBase + (dqs ? '?' + dqs : '');

        // Instant feedback — flip the selected shape and reset dependent filters
        // synchronously, BEFORE the network round-trip. The grid swaps in after.
        collectionUrl = newBase;
        metalSet = {}; styleSet = {};
        root.querySelectorAll('[data-bc-metal-group] .bc-metal-item.is-active, [data-bc-style-group] .bc-style-item.is-active')
          .forEach(function (b) { b.classList.remove('is-active'); });
        root.querySelectorAll('.bc-shape-item').forEach(function (x) { x.classList.toggle('is-active', x === a); });
        initData.currentShape = label;
        refreshHeadings();

        gridLoading(true);
        // Fetch the grid PLUS the breadcrumbs + banner (title) sections so the
        // header updates to the new collection — not just the products.
        var _prefix = sectionId.substring(0, sectionId.lastIndexOf('__'));
        var _bcId = _prefix + '__breadcrumbs';
        var _bannerId = _prefix + '__banner';
        // Build the URL with RAW commas in `sections=` — URLSearchParams encodes
        // them to %2C, which the Section Rendering API rejects (→ fetch fails →
        // full-page fallback). The section ids contain only URL-safe chars.
        var _fetchUrl = newBase + '?' + fetchP.toString() + '&sections=' + [sectionId, _bcId, _bannerId].join(',');
        var seq = ++swapSeq;
        fetch(_fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (j) {
            var gridDoc = new DOMParser().parseFromString(j[sectionId] || '', 'text/html');
            return preloadFirstRow(gridDoc).then(function () {
              if (seq !== swapSeq) {
                // A newer click owns the grid. If it stayed on this collection, the title and
                // breadcrumb only this response carries must still land.
                if (collectionUrl === newBase) { swapSection(_bcId, j[_bcId]); swapSection(_bannerId, j[_bannerId]); }
                return;
              }
              applySwap(gridDoc, displayUrl);
              swapSection(_bcId, j[_bcId]);
              swapSection(_bannerId, j[_bannerId]);
            });
          })
          .catch(function () { if (seq === swapSeq) window.location.href = displayUrl; });
      });
    });

    /* ---- metal / style toggles (live) ---- */
    root.querySelectorAll('[data-bc-metal-group] .bc-metal-item').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var v = btn.getAttribute('data-value');
        if (metalSet[v]) { delete metalSet[v]; btn.classList.remove('is-active'); }
        else { metalSet[v] = true; btn.classList.add('is-active'); }
        refreshHeadings(); applyFilters(false);
      });
    });
    root.querySelectorAll('[data-bc-style-group] .bc-style-item').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var v = btn.getAttribute('data-value');
        if (styleSet[v]) { delete styleSet[v]; btn.classList.remove('is-active'); }
        else { styleSet[v] = true; btn.classList.add('is-active'); }
        refreshHeadings(); applyFilters(false);
      });
    });

    /* ---- chip removal (delegated) ---- */
    root.addEventListener('click', function (e) {
      var chip = e.target.closest('.bc-chip');
      if (!chip) return;
      e.preventDefault();
      if (chip.hasAttribute('data-bc-carat-chip')) { setCarat(DEFAULT_CARAT, true); return; }
      var param = chip.getAttribute('data-bc-remove-param');
      var val = chip.getAttribute('data-bc-remove-value');
      if (param === metalParam && val) {
        delete metalSet[val];
        var mb = root.querySelector('[data-bc-metal-group] .bc-metal-item[data-value="' + val + '"]');
        if (mb) mb.classList.remove('is-active');
      } else if (param === styleParam && val) {
        delete styleSet[val];
        var sb = root.querySelector('[data-bc-style-group] .bc-style-item[data-value="' + val + '"]');
        if (sb) sb.classList.remove('is-active');
      }
      refreshHeadings(); applyFilters(false);
    });

    /* ---- carat (client-side only) ---- */
    // Carat is a display control, not a Shopify filter, so its chip is managed
    // client-side (server-rendered chips only know metal/style/price). Shows a
    // removable "N Ct" chip alongside the metal/style chips; ✕ resets to default.
    function updateCaratChip(caratStr) {
      var chipsWrap = root.querySelector('[data-bc-chips]');
      if (!chipsWrap) return;
      var existing = chipsWrap.querySelector('[data-bc-carat-chip]');
      if (caratStr === DEFAULT_CARAT) {
        if (existing) existing.remove();
      } else {
        if (!existing) {
          existing = document.createElement('button');
          existing.type = 'button';
          existing.className = 'bc-chip';
          existing.setAttribute('data-bc-carat-chip', '');
          chipsWrap.appendChild(existing);
        }
        existing.innerHTML = '<span class="bc-chip__label">' + caratStr + ' Ct</span><span class="bc-chip__x" aria-hidden="true">×</span>';
      }
      chipsWrap.hidden = chipsWrap.querySelectorAll('.bc-chip').length === 0;
    }
    function setCarat(caratStr, persist) {
      caratBtns.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-carat') === caratStr); });
      var cv = root.querySelector('[data-bc-carat-value]');
      if (cv) cv.textContent = caratStr + ' Ct';
      repriceAll(caratStr);
      updateCaratChip(caratStr);
      if (persist) {
        try { sessionStorage.setItem(CARAT_KEY, caratStr); } catch (e) {}
        var u = new URL(window.location.href);
        if (caratStr === DEFAULT_CARAT) u.searchParams.delete('display_carat');
        else u.searchParams.set('display_carat', caratStr);
        history.replaceState({}, '', u.pathname + (u.search ? u.search : '') + u.hash);
      }
    }
    caratBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { setCarat(btn.getAttribute('data-carat'), true); });
    });

    /* ---- sort ---- */
    if (sortSel) sortSel.addEventListener('change', function () { applyFilters(false); });

    /* ---- grid / list view toggle ---- */
    // Toggles a class on <body> (a stable ancestor of the grid) so the choice
    // survives AJAX grid swaps; CSS switches the column count. Persisted.
    var viewBtns = root.querySelectorAll('[data-bc-view-toggle] .bc-view-btn');
    function setView(view, persist) {
      document.body.classList.toggle('bc-view-list', view === 'list');
      viewBtns.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-view') === view); });
      if (persist) { try { localStorage.setItem('bc_view', view); } catch (e) {} }
    }
    viewBtns.forEach(function (b) {
      b.addEventListener('click', function () { setView(b.getAttribute('data-view'), true); });
    });
    var savedView = 'grid';
    try { savedView = localStorage.getItem('bc_view') || 'grid'; } catch (e) {}
    setView(savedView, false);

    /* ---- load more (client-side reveal, 24 at a time) ---- */
    var LOAD_STEP = 24;
    var shownCount = LOAD_STEP;

    function loadMoreWrap() { return document.querySelector('[data-bc-loadmore-wrap]'); }
    function nextPageUrl() {
      var wrap = loadMoreWrap();
      return (wrap && wrap.getAttribute('data-bc-next-url')) || null;
    }
    // Absorb the sibling entries of freshly inserted cards so metal swatches and
    // carat re-pricing keep working on appended / swapped cards. Called AFTER the
    // fetched cards are in the document: their script.bc-card-siblings nodes
    // carry the entries (no need to regex-parse the fetched page-wide map).
    function mergeSiblingMap(doc) {
      getSiblingMap();
    }
    function applyLoadMore(reset) {
      if (reset) shownCount = LOAD_STEP;
      var items = document.querySelectorAll('#product-grid .grid__item');
      for (var i = 0; i < items.length; i++) { items[i].style.display = i < shownCount ? '' : 'none'; }
      var wrap = loadMoreWrap();
      if (wrap) {
        var loaded = items.length;
        // Server-rendered total across ALL paginate pages of the current view
        // (raw DOM count only sees the pages fetched so far).
        var total = parseInt(wrap.getAttribute('data-bc-total-items') || '', 10);
        if (!isFinite(total) || total < loaded) total = loaded;
        var shown = Math.min(shownCount, loaded);
        var shownEl = wrap.querySelector('[data-bc-shown]');
        var totalEl = wrap.querySelector('[data-bc-total]');
        if (shownEl) shownEl.textContent = shown;
        if (totalEl) totalEl.textContent = total;
        wrap.hidden = shown >= loaded && !nextPageUrl();
      }
      initCardCarousels();
      initCardMetals();
      initDesktopCards();
    }
    // Fetch the next paginate page and append its items. paginate.next.url
    // (rendered into data-bc-next-url) carries every active filter param, so
    // the appended page always matches the current view.
    var loadingMore = false;
    function fetchNextPage(done) {
      var wrap = loadMoreWrap();
      var next = wrap && wrap.getAttribute('data-bc-next-url');
      if (!next || loadingMore) { if (done) done(false); return; }
      loadingMore = true;
      var btn = wrap.querySelector('[data-bc-loadmore]');
      if (btn) btn.disabled = true;
      suppressGridObserver();
      window.__bcGridEpoch = (window.__bcGridEpoch || 0) + 1;
      var epoch = window.__bcGridEpoch;
      var current = true;
      fetchSection(next)
        .then(function (doc) {
          if (window.__bcGridEpoch !== epoch) { current = false; return; } // the grid was swapped meanwhile
          var grid = document.getElementById('product-grid');
          var freshItems = doc.querySelectorAll('#product-grid .grid__item');
          if (grid && freshItems.length) {
            var frag = document.createDocumentFragment();
            Array.prototype.forEach.call(freshItems, function (item) {
              item.classList.remove('scroll-trigger', 'animate--slide-in');
              item.removeAttribute('data-cascade');
              item.style.removeProperty('--animation-order');
              stripReveal(item);
              item.style.display = 'none'; // revealed by applyLoadMore below
              item.querySelectorAll('img').forEach(function (img) {
                img.setAttribute('loading', 'lazy');
                // Strip the LCP 'high' hint; keep the secondary image's 'low'.
                if (img.getAttribute('fetchpriority') !== 'low') img.removeAttribute('fetchpriority');
              });
              frag.appendChild(item);
            });
            grid.appendChild(frag);
            mergeSiblingMap(doc);
            repriceAll(currentCarat());
          }
          // Advance (or clear) the next-page pointer from the fetched page.
          var freshWrap = doc.querySelector('[data-bc-loadmore-wrap]');
          var newNext = freshWrap && freshWrap.getAttribute('data-bc-next-url');
          if (newNext) wrap.setAttribute('data-bc-next-url', newNext);
          else wrap.removeAttribute('data-bc-next-url');
        })
        .catch(function () { current = false; })
        .then(function () {
          loadingMore = false;
          if (btn) btn.disabled = false;
          applyLoadMore(false);
          releaseGridObserver();
          if (done) done(current);
        });
    }
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-bc-loadmore]');
      if (!b) return;
      e.preventDefault();
      shownCount += LOAD_STEP;
      applyLoadMore(false);
      var loaded = document.querySelectorAll('#product-grid .grid__item').length;
      if (shownCount > loaded) fetchNextPage();
    });
    applyLoadMore(true);
    setTimeout(function () { applyLoadMore(false); }, 700);

    /* ---- desktop: coming back from a product page returns to the same card ----
       Back reloads this page with only the first LOAD_STEP cards, so a shopper who had
       pressed Load More lost their place. Remember how far they got, re-open that many
       cards once the page's own grid swap has landed, then scroll back. */
    if (bcDeskMQ.matches) {
      var RESTORE_KEY = 'bc-grid-restore';
      var gridKey = function () { return window.location.pathname + window.location.search; };
      window.addEventListener('pagehide', function () {
        try {
          if (shownCount > LOAD_STEP) {
            sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ u: gridKey(), n: shownCount, y: window.scrollY }));
          } else {
            sessionStorage.removeItem(RESTORE_KEY);
          }
        } catch (e) {}
      });
      (function restoreGrid() {
        var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        if (!nav || nav.type !== 'back_forward') return;
        var saved = null;
        try { saved = JSON.parse(sessionStorage.getItem(RESTORE_KEY)); } catch (e) {}
        if (!saved || saved.u !== gridKey() || !(saved.n > LOAD_STEP)) return;
        var moved = false;
        var onMove = function () { moved = true; };
        ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (t) {
          window.addEventListener(t, onMove, { once: true, passive: true });
        });
        var finish = function () {
          applyLoadMore(false);
          if (!moved) window.scrollTo({ top: saved.y, behavior: 'instant' });
        };
        var grow = function () {
          shownCount = saved.n;
          var loaded = document.querySelectorAll('#product-grid .grid__item').length;
          if (loaded >= shownCount || !nextPageUrl()) { finish(); return; }
          fetchNextPage(function (ok) { if (ok) grow(); else finish(); });
        };
        var waitForGrid = function () {
          if (document.documentElement.classList.contains('bc-dedup-pending')) setTimeout(waitForGrid, 50);
          else setTimeout(grow, 0);
        };
        waitForGrid();
      })();
    }

    /* ---- reset ---- */
    if (resetBtn) resetBtn.addEventListener('click', function () {
      metalSet = {}; styleSet = {};
      root.querySelectorAll('[data-bc-metal-group] .bc-metal-item.is-active, [data-bc-style-group] .bc-style-item.is-active')
        .forEach(function (b) { b.classList.remove('is-active'); });
      refreshHeadings(); applyFilters(false);
    });

    /* ---- apply / view results ---- */
    if (applyBtn) applyBtn.addEventListener('click', function () { applyFilters(true); });

    /* ---- back / forward ---- */
    window.addEventListener('popstate', function () { window.location.reload(); });

    /* ---- initial state ---- */
    var startCarat = url.searchParams.get('display_carat');
    if (!startCarat) { try { startCarat = sessionStorage.getItem(CARAT_KEY); } catch (e) {} }
    if (!startCarat) startCarat = DEFAULT_CARAT;
    refreshHeadings();
    syncResultCount();
    setTimeout(syncResultCount, 500);
    // The section's product count can update asynchronously (the theme re-renders
    // to primary-only after load), so track it rather than reading it once.
    var __countSrc = document.getElementById('ProductCountDesktop')
                  || document.getElementById('ProductCount')
                  || document.querySelector('.product-count__text');
    if (__countSrc && 'MutationObserver' in window) {
      new MutationObserver(syncResultCount).observe(__countSrc, { childList: true, characterData: true, subtree: true });
    }

    function firstPrice() {
      if (window.BCPricing && typeof window.BCPricing.lookupStoneCents === 'function') {
        setCarat(startCarat, url.searchParams.get('display_carat') ? false : (startCarat !== DEFAULT_CARAT));
      } else {
        setTimeout(firstPrice, 60);
      }
    }
    firstPrice();

    /* ---- re-price after grid swaps we did NOT trigger (e.g. shape block) ---- */
    var wrapper = document.querySelector('.product-grid-container-wrapper');
    if (wrapper && 'MutationObserver' in window) {
      __gridTarget = wrapper;
      __gridMO = new MutationObserver(function (muts) {
        if (suppressObserver) return;
        // Only reprice when actual element nodes are added (a real grid swap),
        // never on text/attribute/lazy-image mutations — and repriceAll itself
        // detaches this observer while it writes, so no self-retrigger.
        var added = [];
        muts.forEach(function (m) {
          Array.prototype.forEach.call(m.addedNodes, function (n) {
            if (n.nodeType === 1 && !n.hasAttribute('data-bc-desktop-slide')) added.push(n);
          });
        });
        if (!added.length) return;
        // Only new grid items start the list over. Any other node added inside a card must
        // not collapse what the shopper already opened with Load More.
        var newGrid = added.some(function (n) { return n.matches('.grid__item') || n.querySelector('.grid__item'); });
        repriceAll(currentCarat());
        applyLoadMore(newGrid);
      });
      __gridMO.observe(wrapper, { childList: true, subtree: true });
    }

    /* ---- Desktop (>=750px): Keyzar-style shape row + pill bar + custom sort --
       The mobile drawer's interactive group nodes are RELOCATED into the desktop
       shape row / pill popovers (matchMedia), so the existing click/input handlers
       (bound above) keep working. On <750px they move back into the drawer. */
    var desktopWrap = root.querySelector('[data-bc-desktop]');
    if (desktopWrap) {
      var shapeRowHost = desktopWrap.querySelector('[data-bc-desktop-shaperow]');
      var shapeGrid = root.querySelector('.bc-shape-grid');
      var dPills = root.querySelectorAll('[data-bc-desktop-pills] [data-bc-pill]');
      var sortWrap = desktopWrap.querySelector('[data-bc-desktop-sort]');
      var sortToggle = sortWrap && sortWrap.querySelector('[data-bc-sort-toggle]');
      var sortLabel = sortWrap && sortWrap.querySelector('[data-bc-sort-label]');
      var sortMenu = sortWrap && sortWrap.querySelector('[data-bc-sort-menu]');

      function groupNodeFor(name) {
        if (name === 'metal') return root.querySelector('[data-bc-metal-group]');
        if (name === 'style') return root.querySelector('[data-bc-style-group]');
        if (name === 'carat') return root.querySelector('[data-bc-carat-group]');
        return null;
      }
      function recordHome(node) { if (node && !node.__bcHome) node.__bcHome = node.parentNode; }
      recordHome(shapeGrid);
      dPills.forEach(function (pill) { recordHome(groupNodeFor(pill.getAttribute('data-bc-pill'))); });

      function relocateDesktop() {
        if (shapeGrid && shapeRowHost && shapeGrid.parentNode !== shapeRowHost) shapeRowHost.appendChild(shapeGrid);
        dPills.forEach(function (pill) {
          var body = pill.querySelector('[data-bc-popover-body]');
          var node = groupNodeFor(pill.getAttribute('data-bc-pill'));
          if (node && body && node.parentNode !== body) body.appendChild(node);
        });
      }
      function relocateMobile() {
        if (shapeGrid && shapeGrid.__bcHome && shapeGrid.parentNode !== shapeGrid.__bcHome) shapeGrid.__bcHome.appendChild(shapeGrid);
        dPills.forEach(function (pill) {
          var node = groupNodeFor(pill.getAttribute('data-bc-pill'));
          if (node && node.__bcHome && node.parentNode !== node.__bcHome) node.__bcHome.appendChild(node);
        });
        closeAllPopovers();
      }

      /* ---- popover open/close (one at a time) ---- */
      function closeAllPopovers(except) {
        dPills.forEach(function (pill) {
          if (pill === except) return;
          var pop = pill.querySelector('[data-bc-popover]');
          var tgl = pill.querySelector('[data-bc-pill-toggle]');
          if (pop) pop.hidden = true;
          pill.classList.remove('is-pop-open');
          if (tgl) tgl.setAttribute('aria-expanded', 'false');
        });
        if (sortMenu && except !== 'sort') {
          sortMenu.hidden = true;
          sortWrap.classList.remove('is-open');
          if (sortToggle) sortToggle.setAttribute('aria-expanded', 'false');
        }
      }
      function togglePopover(pill) {
        var pop = pill.querySelector('[data-bc-popover]');
        if (!pop) return;
        var willOpen = pop.hidden;
        closeAllPopovers(willOpen ? pill : null);
        pop.hidden = !willOpen;
        pill.classList.toggle('is-pop-open', willOpen);
        var tgl = pill.querySelector('[data-bc-pill-toggle]');
        if (tgl) tgl.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }

      /* ---- clear a group (reuses the existing state + applyFilters) ---- */
      function clearGroup(name) {
        if (name === 'metal') {
          metalSet = {};
          root.querySelectorAll('[data-bc-metal-group] .bc-metal-item.is-active').forEach(function (b) { b.classList.remove('is-active'); });
          refreshHeadings(); applyFilters(false);
        } else if (name === 'style') {
          styleSet = {};
          root.querySelectorAll('[data-bc-style-group] .bc-style-item.is-active').forEach(function (b) { b.classList.remove('is-active'); });
          refreshHeadings(); applyFilters(false);
        } else if (name === 'carat') {
          setCarat(DEFAULT_CARAT, true);
        }
      }

      /* ---- pill labels / active state from current filter state ---- */
      function handleize(v) { return String(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
      function activeValues(groupSel, set) {
        var out = [];
        root.querySelectorAll(groupSel + ' [data-value]').forEach(function (b) {
          var val = b.getAttribute('data-value');
          if (set[val]) out.push({ value: val, label: b.getAttribute('data-label') || val });
        });
        return out;
      }
      function setPill(pill, active, text, swatchClass) {
        var lbl = pill.querySelector('[data-bc-pill-label]');
        if (lbl) lbl.textContent = text;
        pill.classList.toggle('is-active', !!active);
        var sw = pill.querySelector('[data-bc-pill-swatch]');
        if (sw) {
          if (swatchClass) { sw.className = 'bc-dpill__swatch bc-chip__swatch ' + swatchClass; sw.hidden = false; }
          else { sw.hidden = true; }
        }
      }
      function updateDesktopPills() {
        dPills.forEach(function (pill) {
          var name = pill.getAttribute('data-bc-pill');
          if (name === 'metal' || name === 'style') {
            var set = name === 'metal' ? metalSet : styleSet;
            var vals = activeValues(name === 'metal' ? '[data-bc-metal-group]' : '[data-bc-style-group]', set);
            var base = name === 'metal' ? 'Metal' : 'Style';
            if (!vals.length) { setPill(pill, false, base, null); return; }
            var text = vals[0].label + (vals.length > 1 ? ' +' + (vals.length - 1) : '');
            var swatch = name === 'metal' ? 'bc-swatch--' + handleize(vals[0].value) : null;
            setPill(pill, true, text, swatch);
          } else if (name === 'carat') {
            var c = currentCarat();
            var on = c && c !== DEFAULT_CARAT;
            setPill(pill, on, on ? c + ' Ct' : 'Carat', null);
          }
        });
      }

      /* ---- custom sort menu (native <select data-bc-sort> = source of truth) ---- */
      function updateSortLabel() {
        if (sortLabel && sortSel) { var o = sortSel.options[sortSel.selectedIndex]; sortLabel.textContent = o ? o.text : ''; }
      }
      function buildSortMenu() {
        if (!sortSel || !sortMenu) return;
        sortMenu.innerHTML = '';
        Array.prototype.forEach.call(sortSel.options, function (opt) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'bc-dsort__opt' + (opt.value === sortSel.value ? ' is-active' : '');
          b.setAttribute('role', 'option');
          b.setAttribute('data-value', opt.value);
          b.setAttribute('aria-selected', opt.value === sortSel.value ? 'true' : 'false');
          b.textContent = opt.text;
          b.addEventListener('click', function () {
            sortSel.value = b.getAttribute('data-value');
            updateSortLabel();
            Array.prototype.forEach.call(sortMenu.children, function (c) {
              var on = c === b;
              c.classList.toggle('is-active', on);
              c.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            closeAllPopovers();
            var ev;
            try { ev = new Event('change', { bubbles: true }); }
            catch (e) { ev = document.createEvent('Event'); ev.initEvent('change', true, true); }
            sortSel.dispatchEvent(ev);
          });
          sortMenu.appendChild(b);
        });
      }

      /* ---- wire pills + sort ---- */
      dPills.forEach(function (pill) {
        var name = pill.getAttribute('data-bc-pill');
        var toggle = pill.querySelector('[data-bc-pill-toggle]');
        var clear = pill.querySelector('[data-bc-pill-clear]');
        if (toggle) toggle.addEventListener('click', function () { togglePopover(pill); });
        if (clear) clear.addEventListener('click', function (e) { e.preventDefault(); clearGroup(name); closeAllPopovers(); });
      });
      if (sortToggle) sortToggle.addEventListener('click', function () {
        var willOpen = sortMenu.hidden;
        closeAllPopovers('sort');
        sortMenu.hidden = !willOpen;
        sortWrap.classList.toggle('is-open', willOpen);
        sortToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
      document.addEventListener('click', function (e) {
        if (!bcIsDesktop) return;
        if (e.target.closest('[data-bc-pill]') || e.target.closest('[data-bc-desktop-sort]')) return;
        closeAllPopovers();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAllPopovers(); });

      /* ---- keep desktop pills / carat in sync by wrapping existing hooks ---- */
      var _refreshHeadings = refreshHeadings;
      refreshHeadings = function () { _refreshHeadings(); updateDesktopPills(); };
      var _setCarat = setCarat;
      setCarat = function (c, p) { _setCarat(c, p); updateDesktopPills(); };

      /* ---- responsive relocation ---- */
      function applyLayout(desktop) {
        bcIsDesktop = desktop;
        if (desktop) relocateDesktop(); else relocateMobile();
        repositionAllMetals();
        updateDesktopPills();
      }
      var mq = window.matchMedia('(min-width: 750px)');
      buildSortMenu();
      updateSortLabel();
      applyLayout(mq.matches);
      if (mq.addEventListener) mq.addEventListener('change', function (e) { applyLayout(e.matches); });
      else if (mq.addListener) mq.addListener(function (e) { applyLayout(e.matches); });
    }
  }

  /* ---- Document-wide bootstrap ----------------------------------------- */
  // Enhance every diamond card under `root` (defaults to the whole document).
  // Safe to call repeatedly — each card function guards against re-enhancing.
  function enhanceCards(root) {
    root = root || document;
    initCardCarousels(root);
    initCardMetals(root);
    initDesktopCards(root);
    // Affirm "as low as" line — derived from each card's live displayed price
    // (needs no pricing lib), so featured / search / related cards show it like
    // collection cards do.
    var cards = root.querySelectorAll('.card-wrapper');
    for (var i = 0; i < cards.length; i++) {
      if (isDiamondCard(cards[i])) setCardAffirm(cards[i]);
    }
    // If the pricing lib + lists are present (collection, or loaded globally),
    // sync carat-linked prices/links too. At default carat this only refreshes
    // links + Affirm; it's a no-op on surfaces without pricing.
    if (window.BCPricing && typeof window.BCPricing.lookupStoneCents === 'function') {
      repriceAll(currentCarat());
    }
  }

  // Idempotent re-scan hook for code that inserts cards after load (grid Load
  // More, section-render swaps, quick-add). Callers pass the freshly-inserted
  // subtree, or nothing to rescan the whole document.
  window.BCCardEnhance = { rescan: function (root) { enhanceCards(root || document); } };

  // Keep the metal-dots row on the correct side of the card across resizes even
  // on surfaces with no drawer (the drawer's own applyLayout does this too).
  function onBcLayoutChange(e) { bcIsDesktop = e.matches; repositionAllMetals(); }
  if (bcLayoutMQ.addEventListener) bcLayoutMQ.addEventListener('change', onBcLayoutChange);
  else if (bcLayoutMQ.addListener) bcLayoutMQ.addListener(onBcLayoutChange);

  // Cards injected AFTER load — Dawn's <product-recommendations> (Related products)
  // and Complementary products fetch + inject their grid post-load, and quick-add
  // clones cards into a modal. The collection grid has its own observer inside
  // init(root); this global one covers every other surface. Debounced via rAF and
  // guarded so it only fires when a .card-wrapper actually enters the DOM;
  // enhanceCards is idempotent, so re-running over already-enhanced cards is safe.
  function watchForInjectedCards() {
    if (!('MutationObserver' in window)) return;
    var pending = false;
    var mo = new MutationObserver(function (muts) {
      if (pending) return;
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && (n.matches('.card-wrapper') || n.querySelector('.card-wrapper'))) {
            pending = true;
            // setTimeout (not rAF) so it still fires if the tab is briefly
            // backgrounded when recommendations inject; rAF is suspended there.
            window.setTimeout(function () {
              pending = false;
              enhanceCards(document);
            }, 50);
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    // Card behavior runs on EVERY page…
    enhanceCards(document);
    watchForInjectedCards();
    // …the filter drawer only where its custom element exists (collection).
    var root = document.querySelector('bc-diamond-filters');
    if (root) init(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
